import { useTranslation } from 'react-i18next';
import { Lightbulb } from 'lucide-react';
import type { RouteLineDisplayMode } from './route-line-display-mode';

interface RouteLineModeToggleButtonProps {
  mode: RouteLineDisplayMode;
  onToggle: () => void;
  /** Vertical position class (default 'bottom-2'); kept separate to avoid Tailwind conflicts. */
  positionClassName?: string;
  testId?: string;
}

/**
 * The route counterpart of HoldsModeToggleButton: same lightbulb in the same
 * place, cycling what a route drawn as a line shows — line + focus, focus
 * alone, line alone, nothing. The icon says how much is on: filled = both,
 * yellow outline = focus only, plain = the line only, dimmed = nothing.
 */
export function RouteLineModeToggleButton({
  mode,
  onToggle,
  positionClassName = 'bottom-2',
  testId = 'cover-route-line-toggle',
}: RouteLineModeToggleButtonProps) {
  const { t } = useTranslation();

  const iconClass =
    mode === 'focusAndLine' ? 'fill-yellow-300 text-yellow-300'
      : mode === 'focus' ? 'text-yellow-300'
      : mode === 'none' ? 'opacity-40'
      : '';

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      title={t(`climbProfile.routeLineMode.${mode}`)}
      aria-label={t(`climbProfile.routeLineMode.${mode}`)}
      className={`absolute ${positionClassName} left-1/2 -translate-x-1/2 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors`}
      data-testid={testId}
    >
      <Lightbulb className={`h-4 w-4 ${iconClass}`} />
    </button>
  );
}
